import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "p-4 space-y-4" }
const _hoisted_2 = { class: "flex space-x-3 items-center" }
const _hoisted_3 = { class: "text-dark-muted ml-4" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("hr", null, null, -1)
const _hoisted_5 = { class: "flex justify-between space-x-3" }
const _hoisted_6 = { class: "space-x-3" }

import BaseModal from '@components/modals/BaseModal.vue';
import { RepairOrderResource } from '@shared/resources/repair-order';
import { computed, ref } from 'vue';
import { isOrder, isRepair, OrderType } from '@shared/types/order-type';
import { ClientResource } from '@shared/resources/client';
import { useValidator } from '@/libs/validator';
import Button from '@ui-kit/controls/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { dateToTimestamp, getLocalDate, timestampToDate } from '@/helpers/datetime';
import { ModalConfirmation, useModalControls, useModalsStore } from '@components/modals';
import { TabData, Tabs } from '@ui-kit/tabs';
import { OrderService } from '@/services/order.service';
import { convertToNull } from '@/helpers/data-transform';

import { OrderCreateUpdateDto, OrderItemDto } from './dto';
import { orderCreateUpdateSchema } from './validation';
import TabItems from './tabs/TabItems.vue';
import TabMain from './tabs/TabMain.vue';
import TabRepairs from './tabs/TabRepairs.vue';
import { countSum } from '@shared/helpers/sum';
import ModalManageAdjustment from './ModalManageAdjustment.vue';
import { coinsFormat, toNumberFormat } from '@shared/helpers/data-converters/number-converters';
import { RepairOrderCreateUpdateDto } from '@shared/dto/repair-order';

interface Props {
  data?: RepairOrderResource;
  type?: OrderType;
  client?: ClientResource;
}
interface Emits {
  (e: 'update'): void;
  (e: 'close'): void;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'ModalManageOrder',
  props: {
    data: null,
    type: null,
    client: null
  },
  emits: ["update", "close"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update'): void;
  (e: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  data?: RepairOrderResource;
  type?: OrderType;
  client?: ClientResource;
}

const { t } = useI18n();
const { close } = useModalControls();
const orderService = new OrderService();
const modalsStore = useModalsStore();





const pending = ref<boolean>(false);
const formData = ref<OrderCreateUpdateDto>({
  is_draft: false,
  type: props.data?.type || props.type || OrderType.REPAIR_ORDER,
  date: props.data?.date ? timestampToDate(props.data?.date) : getLocalDate(),
  car_id: props.data?.car?.id || null,
  client_id: props.data?.client?.id || null,
  notes: props.data?.notes || '',
  adjustment: toNumberFormat(props.data?.adjustment),
  items: props.data?.items?.map((item) => ({
    id: item.item.id,
    qty: item.qty,
    price: item.price,
  })) || [],
  repair_items: props.data?.repair_items?.map((item) => ({
    id: item.repair.id,
    qty: item.qty,
    price: item.price,
  })) || [],
});
const sum = computed(() => {
  let items: OrderItemDto[] = [];
  if (isOrder(formData.value.type)) {
    items = [...items, ...formData.value.items];
  }
  if (isRepair(formData.value.type)) {
    items = [...items, ...formData.value.repair_items];
  }
  return countSum(items, {
    adjustment: formData.value.adjustment,
  });
});

function updateFormData(data: Partial<OrderCreateUpdateDto>) {
  formData.value = {
    ...formData.value,
    ...data,
  };
}

const validator = useValidator({
  data: computed(() => formData.value),
  schema: computed(() => orderCreateUpdateSchema),
});

const tabs = computed<TabData[]>(() => {
  const items = [OrderType.ORDER, OrderType.REPAIR_ORDER].includes(formData.value.type);
  const repairs = [OrderType.REPAIR, OrderType.REPAIR_ORDER].includes(formData.value.type);
  return [
    {
      title: t('pages.orders.tabs.main'),
      name: 'main',
    },
    ...(items ? [{
      title: t('pages.orders.tabs.items'),
      name: 'items',
    }]: []),
    ...(repairs ? [{
      title: t('pages.orders.tabs.repairs'),
      name: 'repairs',
    }]: []),
  ];
});

function manageAdjustment() {
  modalsStore.addModal({
    component: ModalManageAdjustment,
    props: {
      value: formData.value.adjustment,
    },
    events: {
      save(value: number) {
        formData.value.adjustment = value;
      },
    },
  })
}

function save(draft: boolean = false) {
  if (pending.value) return;

  formData.value.is_draft = draft;
  if (validator.validate()) {
    async function callback(resource: RepairOrderResource) {
      if (draft) {
        emit('update');
      } else {
        await orderService.closeOrder(resource.id).send();
        emit('close');
      }
      close();
    }

    const data: RepairOrderCreateUpdateDto = convertToNull({
      ...formData.value,
      adjustment: coinsFormat(formData.value.adjustment),
      date: dateToTimestamp(formData.value.date),
      items: formData.value.items as Array<Required<OrderItemDto>>|undefined,
      repair_items: formData.value.repair_items as Array<Required<OrderItemDto>>|undefined,
    });

    const request = () => {
      pending.value = true;
      if (props.data?.id) {
        orderService
          .update(props.data.id, data)
          .useValidator(validator)
          .send()
          .then(({data}) => callback(data))
          .finally(() => { pending.value = false; });
      } else {
        orderService
          .add(data)
          .useValidator(validator)
          .send()
          .then(({data}) => callback(data))
          .finally(() => { pending.value = false; });
      }
    };

    if (formData.value.is_draft) {
      request();
    } else {
      modalsStore.addModal({
        component: ModalConfirmation,
        props: {
          title: t('pages.orders.close-order-label'),
          description: t('pages.orders.close-order-description'),
          confirmText: t('pages.orders.actions.save_order'),
          confirmationDisableTime: 3,
          confirm() {
            request();
          },
        },
      });
    }
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    size: "xxl",
    title: __props.data?.id ? _ctx.$t('pages.orders.edit-label') : _ctx.$t('pages.orders.create-label'),
    "no-body-padding": ""
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(Tabs), { tabs: _unref(tabs) }, {
          "tabs-end": _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)('pages.orders.sum', {sum: _unref(sum)})), 1),
              _createVNode(Button, {
                onClick: manageAdjustment,
                size: "sm",
                "button-type": "outline"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('pages.orders.adjustment')), 1)
                ]),
                _: 1
              })
            ])
          ]),
          "tab-main": _withCtx(() => [
            _createVNode(TabMain, {
              data: __props.data,
              "form-data": formData.value,
              client: __props.client,
              type: __props.type,
              onUpdate: updateFormData
            }, null, 8, ["data", "form-data", "client", "type"])
          ]),
          "tab-items": _withCtx(() => [
            _createVNode(TabItems, {
              data: __props.data,
              "form-data": formData.value,
              onUpdate: _cache[0] || (_cache[0] = ($event: any) => (updateFormData({items: $event})))
            }, null, 8, ["data", "form-data"])
          ]),
          "tab-repairs": _withCtx(() => [
            _createVNode(TabRepairs, {
              data: __props.data,
              "form-data": formData.value,
              onUpdate: _cache[1] || (_cache[1] = ($event: any) => (updateFormData({repair_items: $event})))
            }, null, 8, ["data", "form-data"])
          ]),
          _: 1
        }, 8, ["tabs"]),
        _hoisted_4,
        _createElementVNode("div", _hoisted_5, [
          _createVNode(Button, {
            loading: pending.value && !formData.value.is_draft,
            onClick: _cache[2] || (_cache[2] = ($event: any) => (save())),
            size: "lg"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('pages.orders.actions.save_order')), 1)
            ]),
            _: 1
          }, 8, ["loading"]),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(Button, {
              onClick: _unref(close),
              size: "lg",
              "button-type": "outline"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('common.actions.cancel')), 1)
              ]),
              _: 1
            }, 8, ["onClick"]),
            _createVNode(Button, {
              loading: pending.value && formData.value.is_draft,
              onClick: _cache[3] || (_cache[3] = ($event: any) => (save(true))),
              size: "lg"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('pages.orders.actions.save_as_draft')), 1)
              ]),
              _: 1
            }, 8, ["loading"])
          ])
        ])
      ])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})